import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';

import { useDownloadLeadsMutation } from 'services/api/leads.api';
import { ExtendedLead } from 'services/types/lead';

interface DownloadLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  businessUnitId: number;
  filterModel: string;
  sortModel: string;
  totalLeads: number;
}

const SPACE_USAGE_BYTES_PER_LEAD_BASIC = 200; // ~200b / lead
const SPACE_USAGE_BYTES_PER_LEAD_FULL = 3000; // ~3kb / lead
const ESTIMATED_SECONDS_PER_REQUEST_BASIC = 0.5; // Approximate rate of processing
const ESTIMATED_SECONDS_PER_REQUEST_FULL = 4; // Approximate rate of processing
const PAGE_SIZE = 500;

const formatBytes = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) {
    return `${mb.toFixed(2)} MB`;
  }
  if (mb < 10) {
    return `${mb.toFixed(1)} MB`;
  }
  return `${Math.round(mb)} MB`;
};

const formatTime = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)} seg`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
};

const DownloadLeadsDialog: React.FC<DownloadLeadsDialogProps> = ({
  open,
  onClose,
  businessUnitId,
  filterModel,
  sortModel,
  totalLeads,
}) => {
  const [fullDownload, setFullDownload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadLeads] = useDownloadLeadsMutation();

  const expectedRequests = Math.ceil(totalLeads / PAGE_SIZE);

  const sizeBytesBasic = totalLeads * SPACE_USAGE_BYTES_PER_LEAD_BASIC;
  const sizeBytesFull = totalLeads * SPACE_USAGE_BYTES_PER_LEAD_FULL;
  const secondsBasic = expectedRequests * ESTIMATED_SECONDS_PER_REQUEST_BASIC;
  const secondsFull = expectedRequests * ESTIMATED_SECONDS_PER_REQUEST_FULL;

  // eslint-disable-next-line no-await-in-loop
  const handleDownload = async () => {
    try {
      setDownloading(true);
      setProgress(0);
      let page = 0;
      let hasMore = true;
      const allLeads: ExtendedLead[] = [];

      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const response = await downloadLeads({
          businessUnitId,
          filterModel,
          paginationModel: JSON.stringify({ page, pageSize: PAGE_SIZE }),
          includeFullDetails: fullDownload,
          sortModel,
        }).unwrap();

        allLeads.push(...response.leads);
        hasMore = response.leads.length === PAGE_SIZE;
        page += 1;
        setProgress((page / expectedRequests) * 100);
      }

      // Generate CSV
      const headers = [
        'ID',
        'Unidad Negocio',
        'ID Cliente',
        'Link',
        'Nombre',
        'Apellido',
        'Teléfono',
        'Email',
        'Origen',
        'Campaña',
        'url origen',
        'UTM source',
        'Actualización CRM',
        'Marca inicial',
        'Modelo inicial',
        'Creación',
        'Mensajes',
        'Mensajes del cliente',
        'Total mensajes (histórico)',
        'Total mensajes del cliente (histórico)',
        'Total de mensajes fallidos',
        'Descartado',
        ...(fullDownload
          ? [
              'Usuario asignado',
              'Fecha de visita',
              'Motivo de término',
              'Payload CRM',
              'Respuesta CRM',
              'Información inicial',
            ]
          : []),
      ];

      const rows = allLeads.map((lead) => {
        const values = [
          lead.id,
          lead.client?.businessUnit?.name,
          lead.clientId,
          `${window.location.origin}/clients/${lead.client?.id}`,
          lead.client?.firstName,
          lead.client?.lastName,
          lead.client?.phone,
          lead.client?.email,
          lead.source,
          lead.outboundCampaignId,
          lead.url,
          lead.UTM?.source,
          lead.CRMUpdateAt ? new Date(lead.CRMUpdateAt).toLocaleString('es-CL', { hour12: false }) : '',
          lead.initialBrandOfInterest,
          lead.initialModelOfInterest,
          new Date(lead.createdAt).toLocaleString('es-CL', { hour12: false }),
          lead.messagesCount,
          lead.clientMessagesCount,
          lead.client?.messagesCount,
          lead.client?.clientMessagesCount,
          lead.client?.failedMessagesCount,
          lead.discarded,
          ...(fullDownload
            ? [
                lead.client?.user?.name,
                lead.visitDate ? new Date(lead.visitDate).toLocaleString('es-CL', { hour12: false }) : '',
                lead.endReason,
                lead.CRMCreationPayload ? JSON.stringify(lead.CRMCreationPayload) : '',
                lead.CRMCreationResponse ? JSON.stringify(lead.CRMCreationResponse) : '',
                lead.webhookBodyAtCreation ? JSON.stringify(lead.webhookBodyAtCreation) : '',
              ]
            : []),
        ];

        return values
          .map((value) => {
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(';') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(';');
      });

      const csvContent = [headers.join(';'), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading leads:', error);
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Descargar leads</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography>
            Se descargará un archivo CSV (compatible con excel) tomando todos los prospectos con los filtros aplicados
            (Fechas, Campaña, Unidad de negocio)
          </Typography>
          <Typography mb={2} mt={1}>
            <strong>Total de leads: </strong>
            {totalLeads}
          </Typography>
          <FormControl>
            <RadioGroup
              value={fullDownload ? 'full' : 'basic'}
              onChange={(e) => setFullDownload(e.target.value === 'full')}
            >
              <FormControlLabel
                value="basic"
                control={<Radio />}
                label={`Información básica (~${formatBytes(sizeBytesBasic)}, ~${formatTime(secondsBasic)})`}
              />
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={`Información completa (~${formatBytes(sizeBytesFull)}, ~${formatTime(secondsFull)})`}
              />
            </RadioGroup>
          </FormControl>
          {downloading && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Procesando... {Math.round(progress)}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={() => {
            // eslint-disable-next-line no-console
            handleDownload().catch(console.error);
          }}
          variant="contained"
          disabled={downloading}
        >
          {downloading ? 'Descargando...' : 'Descargar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadLeadsDialog;
